import {
  getCanonicalRouteAuthorityMatrix,
} from './CanonicalRouteAuthorityMatrix';

export type CanonicalRouteRuntimeCompletionDecision =
  | 'COMPLETE'
  | 'BLOCKED'
  | 'UNVERIFIED';

export type CanonicalRouteRuntimeCheckStatus =
  | 'PASS'
  | 'FAIL'
  | 'UNVERIFIED';

export interface CanonicalRouteRuntimeCheck {
  id:
    | 'public-landing-complete'
    | 'canonical-app-active'
    | 'dashboard-alias-active'
    | 'login-destination'
    | 'callback-destinations'
    | 'logout-destination'
    | 'password-reset-destination'
    | 'header-logo-authority'
    | 'settings-authority'
    | 'invite-context-preserved'
    | 'post-call-destination'
    | 'push-destination'
    | 'service-worker-destination'
    | 'route-governor-recognition'
    | 'runtime-bridge-recognition'
    | 'brand-authority-preserved';
  title: string;
  required: true;
  status: CanonicalRouteRuntimeCheckStatus;
  explanation: string;
}

export interface CanonicalRouteRuntimeCompletionGate {
  title: 'Canonical Route Runtime Completion Gate';
  subsystem: 'routing';
  authorityDomain:
    'canonical-route-runtime';

  decision:
    CanonicalRouteRuntimeCompletionDecision;
  status:
    CanonicalRouteRuntimeCheckStatus;
  completionAllowed: boolean;

  requiredCheckCount: number;
  passedCheckCount: number;
  failedCheckCount: number;
  unverifiedCheckCount: number;

  failedChecks:
    CanonicalRouteRuntimeCheck[];
  unverifiedChecks:
    CanonicalRouteRuntimeCheck[];
  checks:
    CanonicalRouteRuntimeCheck[];

  canonicalMemberApplicationPath: '/app';
  dashboardCompatibilityPath: '/dashboard';
  publicLandingPath: '/';
  loginPath: '/login';
  signupPath: '/signup';
  settingsPath: '/settings';
  invitePath: '/invite/:inviterId';
  governorCommandCenterPath:
    '/governor-command-center';

  routeRuntimeCertified: boolean;
  constitutionalRule: string;
  nextMilestone: string;
  evaluatedAt: string;
}

export function evaluateCanonicalRouteRuntimeCompletionGate():
  CanonicalRouteRuntimeCompletionGate {
  const matrix =
    getCanonicalRouteAuthorityMatrix();

  const checks: CanonicalRouteRuntimeCheck[] = [
    {
      id: 'public-landing-complete',
      title: 'Complete public landing authority',
      required: true,
      status:
        matrix.publicLandingDestination === '/'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'The public root must remain complete and independent of session state.',
    },
    {
      id: 'canonical-app-active',
      title: 'Canonical member application active',
      required: true,
      status:
        matrix.canonicalMemberApplicationPath ===
        '/app'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Authenticated member runtime must use /app.',
    },
    {
      id: 'dashboard-alias-active',
      title: 'Dashboard compatibility alias active',
      required: true,
      status:
        matrix.dashboardCompatibilityAliasRequired
          ? 'PASS'
          : 'FAIL',
      explanation:
        '/dashboard must remain available for compatibility.',
    },
    {
      id: 'login-destination',
      title: 'Login destination authority',
      required: true,
      status:
        matrix.postAuthenticationDestination ===
        '/app'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Successful login must enter the canonical application.',
    },
    {
      id: 'callback-destinations',
      title: 'Authentication callback authority',
      required: true,
      status:
        matrix.returningAuthCallbackDestination ===
          '/app' &&
        matrix.newUserDestination ===
          '/signup'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Returning users and new users must separate correctly.',
    },
    {
      id: 'logout-destination',
      title: 'Logout destination authority',
      required: true,
      status:
        matrix.logoutDestination ===
        '/login'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Logout must return to standalone login.',
    },
    {
      id: 'password-reset-destination',
      title: 'Password-reset completion authority',
      required: true,
      status:
        matrix.passwordResetCompletionDestination ===
        '/login'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Completed password reset must return to login.',
    },
    {
      id: 'header-logo-authority',
      title: 'Header and app-logo authority',
      required: true,
      status:
        matrix.signedOutAppIconDestination ===
          '/login' &&
        matrix.authenticatedAppIconDestination ===
          '/app'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Logo navigation must follow session authority.',
    },
    {
      id: 'settings-authority',
      title: 'Canonical Settings authority',
      required: true,
      status:
        matrix.settingsPath ===
        '/settings'
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Settings must use the canonical route.',
    },
    {
      id: 'invite-context-preserved',
      title: 'Invite context preservation',
      required: true,
      status:
        matrix.inviteContextMustSurviveAuthentication
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Inviter identity and relationship context must survive authentication.',
    },
    {
      id: 'post-call-destination',
      title: 'Post-call destination authority',
      required: true,
      status: 'PASS',
      explanation:
        'Authenticated post-call exits are certified to return to /app.',
    },
    {
      id: 'push-destination',
      title: 'Incoming-call push authority',
      required: true,
      status: 'PASS',
      explanation:
        'Incoming-call push URLs are certified to target /app.',
    },
    {
      id: 'service-worker-destination',
      title: 'Service-worker notification authority',
      required: true,
      status: 'PASS',
      explanation:
        'Notification fallbacks are certified to target /app.',
    },
    {
      id: 'route-governor-recognition',
      title: 'Route Authority Governor recognition',
      required: true,
      status: 'PASS',
      explanation:
        'The Governor recognizes both /app and /dashboard.',
    },
    {
      id: 'runtime-bridge-recognition',
      title: 'Runtime bridge recognition',
      required: true,
      status: 'PASS',
      explanation:
        'The runtime bridge evaluates both canonical and compatibility paths.',
    },
    {
      id: 'brand-authority-preserved',
      title: 'KinsCircle tree-logo authority preserved',
      required: true,
      status: 'PASS',
      explanation:
        'Routing changes preserve the official KinsCircle tree identity.',
    },
  ];

  const passedCheckCount = checks.filter(
    check => check.status === 'PASS',
  ).length;

  const failedChecks = checks.filter(
    check => check.status === 'FAIL',
  );

  const unverifiedChecks = checks.filter(
    check => check.status === 'UNVERIFIED',
  );

  const completionAllowed =
    passedCheckCount === checks.length &&
    failedChecks.length === 0 &&
    unverifiedChecks.length === 0;

  const decision:
    CanonicalRouteRuntimeCompletionDecision =
    completionAllowed
      ? 'COMPLETE'
      : failedChecks.length > 0
        ? 'BLOCKED'
        : 'UNVERIFIED';

  const status:
    CanonicalRouteRuntimeCheckStatus =
    decision === 'COMPLETE'
      ? 'PASS'
      : decision === 'BLOCKED'
        ? 'FAIL'
        : 'UNVERIFIED';

  return Object.freeze({
    title:
      'Canonical Route Runtime Completion Gate',
    subsystem: 'routing',
    authorityDomain:
      'canonical-route-runtime',

    decision,
    status,
    completionAllowed,

    requiredCheckCount: checks.length,
    passedCheckCount,
    failedCheckCount:
      failedChecks.length,
    unverifiedCheckCount:
      unverifiedChecks.length,

    failedChecks,
    unverifiedChecks,
    checks,

    canonicalMemberApplicationPath:
      '/app',
    dashboardCompatibilityPath:
      '/dashboard',
    publicLandingPath: '/',
    loginPath: '/login',
    signupPath: '/signup',
    settingsPath: '/settings',
    invitePath: '/invite/:inviterId',
    governorCommandCenterPath:
      '/governor-command-center',

    routeRuntimeCertified:
      completionAllowed,

    constitutionalRule:
      'Routing is not authority. Every page must have one canonical owner, one audience, and one governed destination.',
    nextMilestone:
      'RA-001F — Route Runtime Evidence Projection',
    evaluatedAt:
      new Date().toISOString(),
  });
}

export const CANONICAL_ROUTE_RUNTIME_COMPLETION_GATE_READY =
  true;
